import { Pipe, PipeTransform } from "@angular/core";
import moment from "moment";
import "moment/locale/es";
import "moment/locale/de";
import "moment/locale/fr";
import "moment/locale/it";
import "moment/locale/ja";
import "moment/locale/pl";
import "moment/locale/tr";
import "moment/locale/zh-cn";
import "moment/locale/nl";
import "moment/locale/pt";
import "moment/locale/sv";
import "moment/locale/ko";
import { formatDistanceToNowStrict, Locale, parseISO } from "date-fns";
import { TranslateService } from "@ngx-translate/core";

import { enUS, es, de, fr, it, ja, pl, tr, zhCN, nl, pt, sv, ko } from "date-fns/locale";

@Pipe({
  name: "date",
  pure: true,
  standalone: false,
})
export class DatePipe implements PipeTransform {
  constructor(private translate: TranslateService) {}

  private normalizeLang(lang?: string): { moment: string; dfns: Locale } {
    const l = (lang || this.translate.currentLang || "en").toLowerCase();

    switch (l) {
      case "es":
        return { moment: "es", dfns: es };
      case "de":
        return { moment: "de", dfns: de };
      case "fr":
        return { moment: "fr", dfns: fr };
      case "it":
        return { moment: "it", dfns: it };
      case "ja":
      case "jp":
        return { moment: "ja", dfns: ja };
      case "pl":
        return { moment: "pl", dfns: pl };
      case "tr":
        return { moment: "tr", dfns: tr };
      case "nl":
        return { moment: "nl", dfns: nl };
      case "pt":
        return { moment: "pt", dfns: pt };
      case "sv":
        return { moment: "sv", dfns: sv };
      case "ko":
        return { moment: "ko", dfns: ko };
      case "zh":
      case "cn":
        return { moment: "zh-cn", dfns: zhCN };
      default:
        return { moment: "en", dfns: enUS };
    }
  }

  transform(date: any, format?: string, chatTimestamp?: boolean, convertTimezone = false, onlyAgoText = false) {
    if (!date) {
      return this.translate.instant("date.not_passed"); // "Date not passed."
    }

    // Firestore Timestamp
    if (date._seconds) date = new Date(date._seconds * 1000);

    // Bluesnap (01-Oct-20)
    if (typeof date === "string" && date.includes("-")) {
      const parts = date.split("-");
      if (parts?.length === 3 && isNaN(parseInt(parts[1] as any))) {
        date = `${parts[1]} ${parts[0]}, ${parts[2]}`;
      }
    }

    const { moment: mLocale, dfns } = this.normalizeLang(this.translate.currentLang);
    moment.locale(mLocale);

    let d: Date;
    if (date instanceof Date) d = date;
    else if (typeof date === "string") {
      d = /^\d{4}-\d{2}-\d{2}/.test(date) ? parseISO(date) : moment(date).toDate();
    } else {
      d = new Date(date);
    }

    if (convertTimezone) {
      d = moment(d).utcOffset(-5, true).toDate();
    }

    const fmt = format || "ll";

    if (chatTimestamp) {
      const currentYear = moment().year();
      const dateYear = moment(d).year();
      const today = moment().format("YYYY-MM-DD");
      const thatDay = moment(d).format("YYYY-MM-DD");

      if (today === thatDay) {
        return moment(d).format("LT");
      }
      const yesterday = moment().subtract(1, "day").format("YYYY-MM-DD");
      if (yesterday === thatDay) {
        const time = moment(d).format("LT");
        // "Yesterday at {{time}}"
        return this.translate.instant("date.yesterday_at", { time });
      }

      // For other dates in chat timestamps, show year conditionally
      if (currentYear !== dateYear) {
        return moment(d).format("MMM Do, YYYY");
      } else {
        return moment(d).format("MMM Do");
      }
    }

    if (onlyAgoText) {
      return formatDistanceToNowStrict(d, { addSuffix: true, locale: dfns });
    }

    return moment(d).format(fmt);
  }
}
